/**
 * The composer's media picker (Emoji · Stickers · GIFs): which tabs exist, the active tab and the search text of each,
 * and keyboard navigation over a sectioned grid (arrows move a highlight; Enter picks it). Pure, with golden vectors.
 */

export type PickerTab = "emoji" | "stickers" | "gifs";
export type GridDirection = "left" | "right" | "up" | "down" | "home" | "end";

export type PickerState = {
  tabs: PickerTab[];
  tab: PickerTab;
  stickerQuery: string;
  gifQuery: string;
  /** Highlighted cell (flat index over the tab's sections), -1 = none (the search field has the keyboard). */
  active: number;
};

export type PickerEvent =
  | { type: "tab"; tab: PickerTab }
  /** Next/previous tab (wraps). */
  | { type: "cycle"; delta: number }
  | { type: "query"; text: string }
  /** Arrow keys over the current grid: `sizes` = item count of each section, `columns` per row. */
  | { type: "move"; dir: GridDirection; sizes: number[]; columns: number }
  /** The grid's content changed: keep the highlight inside it. */
  | { type: "resized"; count: number };

/** Tabs in display order: Emoji always; Stickers and GIFs when the workspace offers them. */
export function pickerTabs(stickers: boolean, gifs: boolean): PickerTab[] {
  const tabs: PickerTab[] = ["emoji"];
  if (stickers) tabs.push("stickers");
  if (gifs) tabs.push("gifs");
  return tabs;
}

/** A fresh picker, reopening on the last tab used when it still exists. */
export function initialPicker(tabs: PickerTab[], lastTab: PickerTab | undefined): PickerState {
  const tab = lastTab !== undefined && tabs.includes(lastTab) ? lastTab : tabs[0] ?? "emoji";
  return { tabs: tabs.length === 0 ? ["emoji"] : tabs, tab, stickerQuery: "", gifQuery: "", active: -1 };
}

export function pickerReducer(s: PickerState, e: PickerEvent): PickerState {
  switch (e.type) {
    case "tab":
      return s.tabs.includes(e.tab) && e.tab !== s.tab ? { ...s, tab: e.tab, active: -1 } : s;
    case "cycle": {
      const i = s.tabs.indexOf(s.tab);
      const n = s.tabs.length;
      const next = s.tabs[(((i + Math.trunc(e.delta)) % n) + n) % n]!;
      return next === s.tab ? s : { ...s, tab: next, active: -1 };
    }
    case "query": {
      if (s.tab === "stickers") return s.stickerQuery === e.text ? s : { ...s, stickerQuery: e.text, active: -1 };
      if (s.tab === "gifs") return s.gifQuery === e.text ? s : { ...s, gifQuery: e.text, active: -1 };
      return s;
    }
    case "move": {
      const active = moveInSections(s.active, e.sizes, e.columns, e.dir);
      return active === s.active ? s : { ...s, active };
    }
    case "resized": {
      const active = e.count === 0 ? -1 : Math.min(s.active, e.count - 1);
      return active === s.active ? s : { ...s, active };
    }
  }
}

/** The query of the current tab. */
export function currentQuery(s: PickerState): string {
  return s.tab === "stickers" ? s.stickerQuery : s.tab === "gifs" ? s.gifQuery : "";
}

/**
 * Arrow-key movement over sections laid out as rows of `columns` cells (each section starts a new row). From -1 (no
 * highlight) any move lands on the first cell. Up from the first row returns -1 (back to the search field); down from
 * the last row stays. Up/down keep the column, clamped to the length of a shorter row.
 */
export function moveInSections(index: number, sizes: number[], columns: number, dir: GridDirection): number {
  const total = sizes.reduce((a, b) => a + Math.max(0, b), 0);
  if (total === 0 || columns < 1) return -1;
  if (index < 0 || index >= total) return dir === "up" ? -1 : 0;
  if (dir === "home") return 0;
  if (dir === "end") return total - 1;
  if (dir === "left") return Math.max(0, index - 1);
  if (dir === "right") return Math.min(total - 1, index + 1);
  // Rows: [start, length] in flat indices.
  const rows: [number, number][] = [];
  let start = 0;
  for (const size of sizes) {
    for (let r = 0; r < size; r += columns) rows.push([start + r, Math.min(columns, size - r)]);
    start += Math.max(0, size);
  }
  const row = rows.findIndex(([s, len]) => index >= s && index < s + len);
  const col = index - rows[row]![0];
  const target = dir === "up" ? row - 1 : row + 1;
  if (target < 0) return -1;
  if (target >= rows.length) return index;
  const [s, len] = rows[target]!;
  return s + Math.min(col, len - 1);
}

/** Flat index → [section, item] (undefined when out of range). */
export function cellAt(index: number, sizes: number[]): [number, number] | undefined {
  if (index < 0) return undefined;
  let rest = index;
  for (let s = 0; s < sizes.length; s++) {
    const size = Math.max(0, sizes[s]!);
    if (rest < size) return [s, rest];
    rest -= size;
  }
  return undefined;
}
